import React, { useState, useEffect, useMemo } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const PERIOD_OPTIONS = [
  { value: 'full', label: 'Full Period' },
  { value: '12', label: '12 Months' },
  { value: '36', label: '36 Months' },
  { value: '60', label: '60 Months' },
];

const CorrelationPage = ({ analyticsParams, paramsKey }) => {
  const [period, setPeriod] = useState('full');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchFn = () => tradingService.analyticsCorrelation({ ...analyticsParams, period });
        const res = await fetchFn();
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, period]);

  const heatmapTrace = useMemo(() => {
    if (!data?.matrix) return null;
    const { index: rowLabels, columns: colLabels, data: matrix } = data.matrix;

    if (!rowLabels.length || !colLabels.length) return null;

    const annotations = [];
    for (let i = 0; i < rowLabels.length; i++) {
      for (let j = 0; j < colLabels.length; j++) {
        const val = matrix[i][j];
        if (val == null) continue;
        annotations.push({
          x: colLabels[j], y: rowLabels[i],
          text: val.toFixed(2),
          font: { size: 10, color: Math.abs(val) > 0.65 ? '#fff' : '#333' },
          showarrow: false, xref: 'x', yref: 'y',
        });
      }
    }

    return {
      trace: {
        z: matrix, x: colLabels, y: rowLabels,
        type: 'heatmap',
        colorscale: [[0, '#2563eb'], [0.5, '#ffffff'], [1, '#dc2626']],
        zmin: -1, zmax: 1, zmid: 0,
        text: matrix.map((row) => row.map((v) => (v != null ? v.toFixed(2) : ''))),
        texttemplate: '%{text}',
        textfont: { size: 9 },
        hovertemplate: '%{y} vs %{x}: %{z:.2f}<extra></extra>',
        showscale: true,
        colorbar: { title: 'Correlation', titleside: 'right', len: 0.8 },
      },
      annotations,
      size: rowLabels.length,
    };
  }, [data]);

  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none w-full';

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  return (
    <div>
      {/* Period Selector */}
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="w-full max-w-xs">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Period</label>
          <select className={selectCls} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {data?.strategies && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing correlations for {data.strategies.length} strateg{data.strategies.length !== 1 ? 'ies' : 'y'}
            {period !== 'full' ? ` (last ${period} months)` : ' (full period)'}
          </p>
        )}
      </div>

      {/* Correlation Heatmap */}
      {heatmapTrace && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Return Correlation Matrix</h3>
          <div className="overflow-x-auto">
            <PlotlyChart
              data={[heatmapTrace.trace]}
              layout={{
                paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                font: { color: '#9ca3af', size: 10 },
                xaxis: { side: 'bottom', tickangle: -45, automargin: true },
                yaxis: { autorange: 'reversed', automargin: true },
                margin: { l: 120, r: 60, t: 30, b: 120 },
                annotations: heatmapTrace.annotations,
              }}
              style={{ minHeight: Math.max(400, heatmapTrace.size * 40 + 150) }}
              interactive
            />
          </div>
        </div>
      )}

      {!heatmapTrace && !loading && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No correlation data available for the selected parameters.
        </div>
      )}
    </div>
  );
};

export default CorrelationPage;
